import { useCallback, useState } from 'react';

import useSystemFeatures from '../system/useSystemFeatures';
import { SYSTEM_FEATURE } from '../../types/system/features';
import RestaurantProductionInventoryService from '../../services/restaurant/RestaurantProductionInventoryService';
import type { RestaurantRecipeAvailability } from '../../types/restaurant/RestaurantProductionInventoryTypes';

interface PendingGuard {
  availability: RestaurantRecipeAvailability;
  proceed: () => void;
}

/**
 * Aviso informativo en el POS normal antes de vender un producto con receta.
 *
 * Solo actúa cuando **el módulo Restaurante está activo en el plan** y la
 * sucursal tiene encendido el flag "Validar existencia antes de preparar"
 * (`validationEnabled`). En cualquier otro caso deja pasar la venta sin tocar
 * nada — no afecta a producción ni al POS de negocios que no usa restaurante.
 */
export const useRecipeStockGuard = () => {
  const { isEnabled } = useSystemFeatures();
  const [pending, setPending] = useState<PendingGuard | null>(null);

  const guard = useCallback(
    async (
      branchId: number | null | undefined,
      productId: number,
      quantity: number,
      onProceed: () => void
    ) => {
      if (!branchId || !isEnabled(SYSTEM_FEATURE.RESTAURANT)) {
        onProceed();
        return;
      }

      try {
        const response =
          await RestaurantProductionInventoryService.checkAvailabilityByProduct(
            branchId,
            productId,
            quantity
          );
        const availability = response.data;

        if (availability?.hasRecipe && availability?.validationEnabled) {
          setPending({ availability, proceed: onProceed });
        } else {
          onProceed();
        }
      } catch {
        // Nunca bloquear la venta por un fallo del chequeo informativo.
        onProceed();
      }
    },
    [isEnabled]
  );

  const confirm = useCallback(() => {
    pending?.proceed();
    setPending(null);
  }, [pending]);

  const cancel = useCallback(() => setPending(null), []);

  return { guard, pending, confirm, cancel };
};

export default useRecipeStockGuard;
